import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FilaRenglon } from '../components/identificacion/fila-renglon';
import {
  BarraAcciones,
  FiltrosDelPanel,
  type FiltrosVisibles,
} from '../components/identificacion/barra-acciones';
import {
  traerPanel,
  decidirFila,
  decidirEnBloque,
  cerrarCarga,
  type PanelDeIdentificacion,
  type DecisionDeFila,
} from '../services/carga-manual-service';

/**
 * El panel de identificación del spec 199 §4.
 *
 * ⚖️ Es una pantalla propia a la que se vuelve, no una bandeja dentro del
 * asistente: con 200 filas nadie termina de confirmar en una sentada. Mientras
 * la carga esté pendiente se puede salir y volver, y la tarjeta de la tienda
 * muestra lo que falta.
 */
export function IdentificacionPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [panel, setPanel] = useState<PanelDeIdentificacion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<{ sinDecidir: number } | null>(null);
  const [filtros, setFiltros] = useState<FiltrosVisibles>({ estado: 'TODOS', busqueda: '' });

  const recargar = useCallback(async () => {
    if (!jobId) return;
    setCargando(true);
    try {
      setPanel(await traerPanel(jobId, filtros));
    } catch {
      toast({
        title: 'No se pudo cargar el panel',
        description: 'Probá de nuevo en un momento.',
        variant: 'destructive',
      });
    } finally {
      setCargando(false);
    }
    // `filtros` va en las dependencias o la pantalla filtra una sola vez y
    // después se queda quieta mientras los botones cambian de color.
  }, [jobId, filtros]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const handleDecidir = async (itemId: string, decision: DecisionDeFila) => {
    setTrabajando(true);
    try {
      await decidirFila(itemId, decision);
      await recargar();
    } catch (error) {
      // El backend explica en castellano por qué no se pudo — el caso típico
      // es que otro renglón de la misma carga ya apunte a ese artículo.
      const mensaje = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast({
        title: 'No se pudo aplicar',
        description: mensaje ?? 'Probá de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setTrabajando(false);
    }
  };

  const handleConfirmarDesde = async (confianza: number) => {
    if (!panel) return;
    setTrabajando(true);
    try {
      const r = await decidirEnBloque(panel.job.id, {
        desdeConfianza: confianza,
        // Sin artículo elegido: cada renglón se confirma contra SU mejor
        // candidato, que el backend resuelve uno por uno.
        decision: { tipo: 'CONFIRMAR' },
      });
      toast({
        title: r.aplicados === 1 ? '1 renglón confirmado' : `${r.aplicados} renglones confirmados`,
        description: r.fallidos.length
          ? `${r.fallidos.length} no se pudieron: ${r.fallidos[0]!.motivo}`
          : undefined,
      });
      await recargar();
    } finally {
      setTrabajando(false);
    }
  };

  const handleCerrar = async (aceptaNuevos: boolean) => {
    if (!jobId) return;
    setTrabajando(true);
    try {
      const r = await cerrarCarga(jobId, aceptaNuevos);
      if (!r.cerrada) {
        setAviso({ sinDecidir: r.sinDecidir });
        return;
      }
      setAviso(null);
      toast({
        title: 'Carga lista',
        description: 'Todos los renglones quedaron decididos.',
      });
      await recargar();
    } finally {
      setTrabajando(false);
    }
  };

  if (cargando && !panel) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Cargando…
      </div>
    );
  }

  if (!panel) {
    return (
      <div className="p-6">
        <p className="text-sm">No se encontró esa carga.</p>
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        {/* La carga manual se lanza desde Compite › Productos › Mis productos. */}
        <Button variant="ghost" size="sm" onClick={() => navigate('/compite/productos?tab=mis')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver a Competidores
        </Button>
        <h1 className="text-xl font-semibold mt-2">
          Identificar los renglones — {panel.job.tienda.name}
        </h1>
        <p className="text-sm text-muted-foreground">
          {panel.job.estado === 'LISTA'
            ? 'Esta carga ya está lista.'
            : 'Se puede salir y volver: la carga queda pendiente hasta que no falte ninguno.'}
        </p>
      </div>

      <FiltrosDelPanel valor={filtros} onCambiar={setFiltros} />

      <BarraAcciones
        panel={panel}
        trabajando={trabajando}
        onConfirmarDesde={(c) => void handleConfirmarDesde(c)}
        onCerrar={() => void handleCerrar(false)}
      />

      <div className="overflow-x-auto">
        <table className="w-full border rounded-md">
          <thead className="bg-muted/50 text-xs text-left">
            <tr>
              <th className="p-2">Lo que se subió</th>
              <th className="p-2 text-right">Precio</th>
              <th className="p-2 text-right">Variación</th>
              <th className="p-2">Candidato en {panel.job.tienda.name}</th>
              <th className="p-2">Estado</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {panel.renglones.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                  Ningún renglón coincide con estos filtros.
                </td>
              </tr>
            ) : (
              panel.renglones.map((r) => (
                <FilaRenglon
                  key={r.id}
                  renglon={r}
                  storeId={panel.job.tienda.id}
                  nombreTienda={panel.job.tienda.name}
                  onDecidir={(d) => void handleDecidir(r.id, d)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/*
        La advertencia del §4.2. Dice exactamente qué va a pasar, y la acción
        recomendada es volver a revisar, no continuar — por eso «Volver a
        revisar» va en `AlertDialogAction`, que es el botón principal, y
        «Registrarlos como nuevos» en el secundario. Es al revés de lo que pide
        el instinto, y es a propósito.
      */}
      <AlertDialog open={aviso != null} onOpenChange={(v) => !v && setAviso(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {aviso?.sinDecidir === 1
                ? 'Queda 1 renglón sin decidir'
                : `Quedan ${aviso?.sinDecidir} renglones sin decidir`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {aviso?.sinDecidir === 1
                ? `Si seguís, ese renglón se registra como un producto nuevo de ${panel.job.tienda.name}`
                : `Si seguís, esos ${aviso?.sinDecidir} se registran como productos nuevos de ${panel.job.tienda.name}`}{' '}
              y {aviso?.sinDecidir === 1 ? 'va' : 'van'} a aparecer en el panel de inferencia.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => void handleCerrar(true)}>
              Registrarlos como nuevos
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => setAviso(null)}>Volver a revisar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
